<template>
  <div>
    <card-dialog
      v-model="modalVisible"
      max-width="650px"
      :title="illnessFormData.id ? $t('edit_illness.dialog.header') : $t('new_illness.selected_player.dialog.header')"
    >
      <v-card-text
        ref="scollArea"
        class="pa-6 droparea"
        @dragenter.prevent="dragenter"
      >
        <validation-observer ref="validation">
          <div
            v-if="player.selected"
            class="rounded-sm grey-bg pa-2 mb-6 text-body-1"
          >
            <v-row
              no-gutters
              align="center"
            >
              <v-col
                cols="3"
                class="px-2 font-weight-medium"
              >
                {{ $t("player.label.title") }}
              </v-col>
              <v-col>
                {{ player.selected.first_name }} {{ player.selected.last_name }}
              </v-col>
              <v-col
                v-if="!illnessFormData.id"
                cols="auto"
                class="px-4"
              >
                <v-btn
                  small
                  text
                  class="text-none"
                  @click="player.selectDialog = true; modalVisible = false;"
                >
                  <v-icon
                    x-small
                    left
                  >
                    fal fa-pen
                  </v-icon>
                  {{ $t('button.change') }}
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <subtitle class="mb-3">
            When did the illness start?
          </subtitle>

          <validation-provider
            v-slot="{ errors }"
            key="illnessFormDate"
            rules="required"
          >
            <v-menu
              v-model="dateSelectorActive"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="illnessFormData.date ? $dayjs(illnessFormData.date).format(getDateTimeFormatForUser('D MMM, YYYY')) : ''"
                  :label="$t('picker.date.placeholder')"
                  outlined
                  hide-details
                  :error-messages="errors"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="illnessFormData.date"
                :first-day-of-week="1"
                no-title
                :max="$dayjs().format('YYYY-MM-DD')"
                @change="dateSelectorActive = false"
              />
            </v-menu>
          </validation-provider>

          <v-switch
            v-if="!illnessFormData.id && illnessFormData.date && $dayjs().diff($dayjs(illnessFormData.date), 'day') > 5"
            v-model="illnessFormData.still_active"
            label="Illness still active?"
            hide-details
            @change="onStillActiveChange"
          />

          <div v-if="!illnessFormData.still_active">
            <subtitle class="mb-3 mt-8">
              {{ $t('dropdown.injury.return_to_competition.title') }}
            </subtitle>

            <validation-provider
              v-slot="{ errors }"
              key="illnessFormCompletedDate"
              rules="required"
            >
              <v-menu
                v-model="completedDateSelectorActive"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="illnessFormData.completed_date ? $dayjs(illnessFormData.completed_date).format(getDateTimeFormatForUser('D MMM, YYYY')) : ''"
                    :label="$t('picker.date.placeholder')"
                    outlined
                    hide-details
                    :error-messages="errors"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="illnessFormData.completed_date"
                  :first-day-of-week="1"
                  no-title
                  :max="$dayjs().format('YYYY-MM-DD')"
                  :min="illnessFormData.date"
                  @change="completedDateSelectorActive = false"
                />
              </v-menu>
            </validation-provider>
          </div>

          <subtitle class="mb-3 mt-8">
            Provide illness details
          </subtitle>

          <validation-provider
            v-slot="{ errors }"
          >
            <v-select
              v-model="illnessFormData.illness_category_id"
              :items="categories"
              item-text="name"
              item-value="id"
              :label="$t('illness.category.label')"
              outlined
              clearable
              :menu-props="{ maxWidth: '600px' }"
              :error-messages="errors"
              @change="onCategoryChange"
            >
              <template v-slot:selection="{ item }">
                {{ item.code }} &middot; {{ item.name }}
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="text-wrap">
                    {{ item.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.code }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-select>
          </validation-provider>

          <validation-provider
            v-slot="{ errors }"
          >
            <v-select
              v-model="illnessFormData.illness_type_id"
              :items="availableTypes"
              item-text="name"
              item-value="id"
              :label="$t('illness.type.label')"
              outlined
              clearable
              :disabled="!illnessFormData.illness_category_id"
              :error-messages="errors"
              :menu-props="{ maxWidth: '600px' }"
              @change="onTypeChange"
            >
              <template v-slot:selection="{ item }">
                {{ item.code }} &middot; {{ item.name }}
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="text-wrap">
                    {{ item.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.code }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-select>
          </validation-provider>

          <validation-provider
            v-slot="{ errors }"
          >
            <v-autocomplete
              v-model="illnessFormData.illness_id"
              :items="allIllnesses"
              item-text="name"
              item-value="id"
              :label="$t('illness.label')"
              outlined
              clearable
              :menu-props="{ maxWidth: '600px' }"
              :error-messages="errors"
              :filter="filterIllness"
              @change="onIllnessChange"
            >
              <template v-slot:selection="{ item }">
                {{ item.code }} &middot; {{ item.name }}
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="text-wrap">
                    {{ item.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.code }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </validation-provider>

          <v-switch
            v-model="illnessFormData.recurrent"
            :label="$t('illness.recurrent.label')"
            hide-details
            class="mb-8 pa-0 mt-0"
          />

          <subtitle class="mb-3 mt-2">
            {{ $t('illness.medicaments.label') }}
          </subtitle>

          <rich-text-editor
            v-model="illnessFormData.medicaments"
            class="mb-8"
          />

          <div v-if="illnessFormData.still_active">
            <subtitle class="mb-3">
              {{ $t('illness.expected_rtc.label') }}
            </subtitle>

            <v-menu
              v-model="expectedRtcSelectorActive"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="illnessFormData.expected_rtc ? $dayjs(illnessFormData.expected_rtc).format(getDateTimeFormatForUser('D MMM, YYYY')) : ''"
                  :label="$t('picker.date.placeholder')"
                  outlined
                  hide-details
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="illnessFormData.expected_rtc"
                :first-day-of-week="1"
                no-title
                :min="$dayjs().format('YYYY-MM-DD')"
                @change="expectedRtcSelectorActive = false"
              />
            </v-menu>
          </div>

          <subtitle class="mb-3 mt-8">
            {{ $t('text_input.provide_short_title.title') }}
          </subtitle>

          <validation-provider
            v-slot="{ errors }"
            key="illnessFormTitle"
            rules="required"
          >
            <v-text-field
              v-model="illnessFormData.title"
              outlined
              :label="$t('illness.title.label')"
              :error-messages="errors"
            />
          </validation-provider>

          <div v-if="!illnessFormData.id">
            <subtitle class="mb-3 mt-3">
              {{ $t('text_input.add_notes.title') }}
            </subtitle>
            <div>
              <rich-text-editor v-model="illnessFormData.note" />
            </div>

            <v-overlay
              color="white"
              light
              :value="fileUpload.isDragging"
              :opacity="0.8"
              @dragleave.prevent="dragleave"
              @dragover.prevent="dragover"
              @drop.prevent="drop"
            >
              <div
                class="mb-4 text-center"
                style="pointer-events: none"
              >
                <v-icon
                  x-large
                  color="grey darken-4"
                >
                  fal fa-upload
                </v-icon>
              </div>
              <div
                class="grey--text text--darken-4"
                style="pointer-events: none"
              >
                {{ $t('card_title.release_to_upload') }}
              </div>
            </v-overlay>

            <v-row
              align="center"
              class="mb-4 mt-2"
            >
              <v-col class="text-body-2 grey--text">
                {{ $t('media_upload.instruction') }}
              </v-col>
              <v-col cols="auto">
                <v-btn
                  color="grey darken-2"
                  text
                  class="text-none"
                  @click="$refs.uploadFile.click()"
                >
                  {{ $t('button.browse') }}
                  <v-icon
                    right
                    small
                  >
                    fal fa-upload
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <input
              id="upload-file"
              ref="uploadFile"
              type="file"
              style="display: none"
              accept="video/*,audio/*,image/*,.doc,.docx,.pptx,.xlsx,.xls,application/pdf,application/octet-stream,.dcm"
              @input="handleFileChange"
            >

            <div>
              <v-row>
                <v-col
                  v-for="(mediaItem, mediaItemIndex) in media"
                  :key="mediaItem.id"
                  cols="6"
                >
                  <media-grid-item
                    :media-item="mediaItem"
                    @preview="preview"
                    @delete="mediaItem.id ? startMediaDelete(mediaItem) : media.splice(mediaItemIndex, 1)"
                  />
                </v-col>
              </v-row>
            </div>
          </div>
        </validation-observer>
      </v-card-text>
      <template v-slot:actions>
        <v-card-actions class="pa-4">
          <div class="flex-grow-1">
            <v-btn
              color="primary"
              block
              large
              :loading="submitting"
              :disabled="media.some(m => m.upload)"
              @click="submit()"
            >
              {{ $t('training_builder.button.save') }}
            </v-btn>
            <v-btn
              v-if="illnessFormData.id"
              color="error"
              block
              text
              class="mt-2"
              :loading="deleting"
              @click="deleteConfirmationDialogVisible = true"
            >
              {{ $t('button.delete') }}
            </v-btn>
          </div>
        </v-card-actions>
      </template>

      <media-viewer-dialog
        :visible.sync="mediaViewerDialog.visible"
        :media="media"
        :view-media-id="mediaViewerDialog.media ? mediaViewerDialog.media.id : undefined"
        @media:updated="updateMedia"
        @media:deleted="removeMedia"
      />

      <delete-confirmation-dialog
        :visible.sync="deleteMedia.dialog"
        :submitting="deleteMedia.deleting"
        @confirmed="destroyMedia()"
      />
    </card-dialog>

    <card-dialog
      v-model="player.selectDialog"
      :title="$t('select_player.dialog.header')"
      max-width="500px"
    >
      <v-card-text class="pa-6">
        <v-text-field
          v-model="player.search"
          dense
          outlined
          append-icon="fal fa-search"
          :placeholder="$t('text_input.search_players.title')"
        />
        <v-list class="v-list--bordered pa-0 ma-0">
          <v-list-item
            v-for="p in filteredPlayerItems"
            :key="p.id"
            @click="selectPlayer(p)"
          >
            <v-list-item-avatar>
              <v-avatar
                size="32"
                class="mr-2"
                color="grey lighten-2"
              >
                <v-img :src="p.photo_url">
                  <template v-slot:placeholder>
                    <v-icon x-small>
                      fal fa-user
                    </v-icon>
                  </template>
                </v-img>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-title>
              {{ p.first_name }} {{ p.last_name }}
            </v-list-item-title>
            <v-list-item-action>
              <v-icon>
                fal fa-arrow-right
              </v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card-text>
    </card-dialog>
  </div>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { EventBus } from '@/plugins/bus'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import { IllnessApiService } from '@/data/services/IllnessApiService'
import _axios from '@/plugins/axios'
import MediaGridItem from '@/components/helpers/MediaGridItem.vue'
import MediaViewerDialog from '@/components/dialogs/MediaViewerDialog.vue'
import { MediaApiService } from '@/data/services/MediaApiService'
import { PlayerApiService } from '@/data/services'
import Subtitle from '@/components/helpers/Subtitle.vue'
import RichTextEditor from '@/components/inputs/RichTextEditor.vue'
import { cloneDeep } from 'lodash'
import { getDateTimeFormatForUser } from '@/utils/helpers'

const emptyIllnessFormData = {
  id: undefined,
  title: '',
  date: null,
  completed_date: null,
  event_id: null,
  illness_category_id: null,
  illness_type_id: null,
  illness_id: null,
  medicaments: '',
  recurrent: false,
  expected_rtc: null,
  still_active: true,
  note: ''
}

export default {
  name: 'IllnessFormDialog',
  components: {
    RichTextEditor,
    Subtitle,
    MediaViewerDialog,
    MediaGridItem,
    DeleteConfirmationDialog,
    CardDialog
  },
  mixins: [deleteConfirmationMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      player: {
        search: '',
        selectDialog: false,
        items: [],
        itemsLoading: false,
        selected: undefined
      },
      illnessFormData: { ...emptyIllnessFormData },
      dateSelectorActive: false,
      completedDateSelectorActive: false,
      expectedRtcSelectorActive: false,
      categories: [],
      submitting: false,
      fileUpload: {
        isDragging: false,
        isUploading: false
      },
      media: [],
      mediaViewerDialog: {
        visible: false,
        media: undefined
      },
      deleteMedia: {
        media: undefined,
        dialog: false,
        deleting: false
      }
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    filteredPlayerItems() {
      if (!this.player.search) return this.player.items

      return this.player.items.filter(p => {
        return (p.first_name + ' ' + p.last_name).toLowerCase().indexOf(this.player.search.toLowerCase()) > -1
      })
    },
    availableTypes() {
      const category = this.categories.find(c => c.id === this.illnessFormData.illness_category_id)
      return category ? category.children : []
    },
    availableIllnesses() {
      const type = this.availableTypes.find(t => t.id === this.illnessFormData.illness_type_id)
      return type ? type.children : []
    },
    allIllnesses() {
      const all = []
      this.categories.forEach(category => {
        if (category.children) {
          category.children.forEach(type => {
            if (type.children) {
              type.children.forEach(illness => {
                all.push({
                  ...illness,
                  categoryId: category.id,
                  typeId: type.id
                })
              })
            }
          })
        }
      })

      return all.filter(illness => {
        if (this.illnessFormData.illness_category_id && illness.categoryId !== this.illnessFormData.illness_category_id) return false
        if (this.illnessFormData.illness_type_id && illness.typeId !== this.illnessFormData.illness_type_id) return false
        return true
      })
    }
  },
  created() {
    EventBus.$on(this.$events.OPEN_DIALOG_ILLNESS_FORM, () => {
      this.player.selectDialog = true
      this.illnessFormData = cloneDeep(emptyIllnessFormData)
      this.illnessFormData.date = this.$dayjs().format('YYYY-MM-DD')
      this.media = []
      this.fetchAllPlayers()
      this.fetchCategories()
    })

    EventBus.$on(this.$events.EDIT_ILLNESS, (illness) => {
      this.player.selected = illness.player
      this.illnessFormData = cloneDeep(emptyIllnessFormData)
      this.illnessFormData.id = illness.id
      this.illnessFormData.title = illness.title
      this.illnessFormData.date = this.$dayjs(illness.date).format('YYYY-MM-DD')

      if (illness.completed_date) {
        this.illnessFormData.completed_date = this.$dayjs(illness.completed_date).format('YYYY-MM-DD')
      }

      this.illnessFormData.still_active = illness.still_active

      if (illness.illness_type) {
        this.illnessFormData.illness_id = illness.illness_type.id
        this.illnessFormData.illness_category_id = illness.illness_type.parent ? (illness.illness_type.parent.parent_id || illness.illness_type.parent.id) : null
        this.illnessFormData.illness_type_id = illness.illness_type.parent_id
      }

      this.illnessFormData.medicaments = illness.medicaments
      this.illnessFormData.recurrent = illness.recurrent
      this.illnessFormData.expected_rtc = illness.expected_rtc ? this.$dayjs(illness.expected_rtc).format('YYYY-MM-DD') : null
      this.illnessFormData.note = illness.note
      this.media = illness.media || []
      this.modalVisible = true
      this.fetchAllPlayers()
      this.fetchCategories()
    })
  },
  methods: {
    getDateTimeFormatForUser,
    async fetchAllPlayers() {
      try {
        this.player.itemsLoading = true
        const result = await PlayerApiService.getSimpleList({ order_by: 'last_name', order: 'ASC' })
        this.player.items = result.data
      } finally {
        this.player.itemsLoading = false
      }
    },
    async fetchCategories() {
      try {
        const result = await IllnessApiService.fetchIllnessCategories()
        this.categories = result.data
      } catch (e) {
      }
    },
    selectPlayer(p) {
      this.player.selected = p
      this.player.selectDialog = false
      this.modalVisible = true
      this.$nextTick(() => {
        this.$refs.validation.reset()
      })
    },
    onCategoryChange() {
      this.illnessFormData.illness_type_id = null
      this.illnessFormData.illness_id = null
    },
    onTypeChange() {
      this.illnessFormData.illness_id = null
    },
    onIllnessChange(illnessId) {
      if (!illnessId) return

      const illness = this.allIllnesses.find(i => i.id === illnessId)
      if (illness) {
        this.illnessFormData.illness_category_id = illness.categoryId
        this.illnessFormData.illness_type_id = illness.typeId
      }
    },
    async submit() {
      if (!(await this.$refs.validation.validate())) {
        this.$vuetify.goTo('.error--text', { container: this.$refs.scollArea, offset: 400 })
        return
      }
      await this.save()
    },
    async save() {
      try {
        this.submitting = true
        const data = {
          player_id: this.player.selected.id,
          title: this.illnessFormData.title,
          date: this.illnessFormData.date,
          completed_date: this.illnessFormData.still_active ? null : this.illnessFormData.completed_date,
          expected_rtc: this.illnessFormData.still_active ? this.illnessFormData.expected_rtc : null,
          illness_type_id: this.illnessFormData.illness_id,
          medicaments: this.illnessFormData.medicaments,
          recurrent: this.illnessFormData.recurrent,
          still_active: this.illnessFormData.still_active,
          note: this.illnessFormData.note,
          media_ids: this.media.filter(m => m.id).map(m => m.id)
        }

        let result
        if (!this.illnessFormData.id) {
          result = await IllnessApiService.createIllness(data)
        } else {
          result = await IllnessApiService.updateIllness(this.illnessFormData.id, data)
        }

        this.$emit('refresh', result.data)
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.illnessFormData.id ? this.$t('alert.illness_update.success') : this.$t('alert.illness_save.success'),
          color: 'success'
        })
        EventBus.$emit(this.illnessFormData.id ? this.$events.ILLNESS_UPDATED : this.$events.ILLNESS_CREATED, result.data)
        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: 'There were some errors saving the illness',
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async handleFileChange() {
      await this.$nextTick()
      const files = [...this.$refs.uploadFile.files]
      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'dkg', 'dcm']
      for (const file of files) {
        const type = file.type
        const extension = file.name.split('.').pop()
        if (type.match('image.*') || type.match('video.*') || type.match('audio.*') || allowedExtensions.indexOf(extension) > -1) {
          this.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            upload: true
          })
        } else {
          this.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            error: 'File type not allowed'
          })
        }
      }
      this.$refs.uploadFile.value = null
      this.processUpload()
    },
    preview(media) {
      this.mediaViewerDialog.visible = true
      this.mediaViewerDialog.media = media
    },
    dragover(e) {
      this.fileUpload.isDragging = true
    },
    dragleave(e) {
      this.fileUpload.isDragging = false
    },
    dragenter(e) {
      this.fileUpload.isDragging = true
    },
    drop(e) {
      this.$refs.uploadFile.files = e.dataTransfer.files
      this.handleFileChange()
      this.fileUpload.isDragging = false
    },
    startMediaDelete(media) {
      this.deleteMedia.media = media
      this.deleteMedia.dialog = true
    },
    async processUpload() {
      for (const file of this.media.filter(m => !!m.upload)) {
        const formData = new FormData()
        formData.append('file', file.file, file.name)
        _axios.post('training-exercises/media-upload', formData, {
          onUploadProgress: (e) => {
            file.progress = Number((e.loaded / e.total) * 100)
          }
        }).then(response => {
          file.upload = false
          Object.assign(file, response.data.media)
        }).catch(() => {
          this.$store.dispatch(this.$storeActions.ALERT, {
            color: 'error',
            text: this.$t('alert.uploading_file.error')
          })
          file.error = this.$t('alert.uploading_file.error')
        })
      }
    },
    async destroyMedia() {
      if (!this.deleteMedia.media) return
      const id = this.deleteMedia.media.id
      try {
        this.deleteMedia.deleting = true
        await MediaApiService.delete(id)
        const index = this.media.findIndex(m => m.id === id)
        if (index > -1) this.media.splice(index, 1)
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.media_delete.success'),
          color: 'success'
        })
        this.deleteMedia.dialog = false
      } finally {
        this.deleteMedia.media = undefined
        this.deleteMedia.deleting = false
      }
    },
    updateMedia(media) {
      const index = this.media.findIndex(m => m.id === media.id)
      if (index > -1) this.media.splice(index, 1, media)
    },
    removeMedia(media) {
      const index = this.media.findIndex(m => m.id === media.id)
      if (index > -1) this.media.splice(index, 1)
    },
    onStillActiveChange(value) {
      if (!value && !this.illnessFormData.completed_date) this.illnessFormData.completed_date = this.$dayjs().format('YYYY-MM-DD')
    },
    filterIllness(item, queryText) {
      const search = queryText.toLowerCase()
      const name = (item.name || '').toLowerCase()
      const code = (item.code || '').toLowerCase()
      return name.includes(search) || code.includes(search)
    }
  }
}
</script>
